import { Injectable, inject, signal } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type SnackKind = 'success' | 'error' | 'info';

export interface Snack {
  id: number;
  kind: SnackKind;
  message: string;
}

/**
 * App-wide action feedback (snackbars) — the single channel for "did that
 * work?" on any API-backed or non-immediate action.
 *
 * Reimplemented from the old MatSnackBar wrapper with the SAME public API
 * (openSnackBar / success / error / warn keep working for legacy callers) but
 * rendered by our own SnackbarHostComponent in the app shell: Apple-styled,
 * kind-colored, auto-dismissing (errors linger longer), max 3 stacked.
 *
 * New code should use the key-based helpers so messages stay translated:
 *   snackbar.successKey('clubSettings.saved')
 *   snackbar.errorKey('common.actionFailed')
 */
@Injectable({ providedIn: 'root' })
export class SnackBarService {
  private readonly translate = inject(TranslateService);

  private seq = 0;
  private readonly _snacks = signal<Snack[]>([]);
  readonly snacks = this._snacks.asReadonly();

  // --- key-based (preferred) ------------------------------------------------
  successKey(key: string, params?: Record<string, unknown>): void {
    this.push('success', this.translate.instant(key, params));
  }
  errorKey(key: string, params?: Record<string, unknown>): void {
    this.push('error', this.translate.instant(key, params));
  }
  infoKey(key: string, params?: Record<string, unknown>): void {
    this.push('info', this.translate.instant(key, params));
  }

  // --- legacy raw-message API (kept for existing callers) --------------------
  openSnackBar(message: string, _actionButtonText?: string): void {
    this.push('info', message);
  }
  success(message: string, _actionButtonText?: string): void {
    this.push('success', message);
  }
  error(message: string, _actionButtonText?: string): void {
    this.push('error', message);
  }
  warn(message: string, _actionButtonText?: string): void {
    this.push('error', message);
  }

  dismiss(id: number): void {
    this._snacks.set(this._snacks().filter((s) => s.id !== id));
  }

  private push(kind: SnackKind, message: string): void {
    const snack: Snack = { id: ++this.seq, kind, message };
    // Newest at the end; cap the stack at 3 (oldest drops).
    this._snacks.set([...this._snacks(), snack].slice(-3));
    const ttl = kind === 'error' ? 6000 : 3500;
    setTimeout(() => this.dismiss(snack.id), ttl);
  }
}
